import React, { useEffect, useState } from 'react';
import axios from '../../config/axiosConfig';
import { Box, Grid, CircularProgress } from '@mui/material';
import MiniExtendedHoursGainers from './MiniExtendedHoursGainers';
import MiniExtendedHoursLosers from './MiniextendedhoursLosers';
import MiniMarketHoursGainers from './MiniMarketHoursGainers';
import MiniMarketHoursLosers from './MiniMarketHoursLosers';

const MiniMarketMovers = () => {
    const [marketStatus, setMarketStatus] = useState('loading'); // 'pre', 'open', 'after', 'loading'
    const [extendedHoursData, setExtendedHoursData] = useState({
        pre: { gainers: [], losers: [] },
        after: { gainers: [], losers: [] }
    });
    const [marketHoursData, setMarketHoursData] = useState({
        gainers: [],
        losers: []
    });
    const [loading, setLoading] = useState(true);

    const getMarketStatus = async () => {
        try {
            const response = await axios.get('/api/exchange-status/NASDAQ/session');
            const session = response.data.session;

            // Map API response to component state
            switch (session) {
                case 'pre-market':
                    return 'pre';
                case 'open':
                    return 'open';
                case 'after-market':
                    return 'after';
                case 'closed-weekend':
                case 'closed-holiday':
                case 'closed':  // Overnight or outside trading hours
                    return 'open'; // Show regular market hours data from last trading day
                default:
                    return 'open';
            }
        } catch (error) {
            console.error('Error fetching market status:', error);
            return 'open'; // Fallback to regular market hours on error
        }
    };

    // Fetch data based on market status
    const fetchData = async (status) => {
        setLoading(true);
        try {
            if (status === 'pre') {
                // Fetch pre-market data
                const response = await axios.get('/api/extended-hours/movers/pre');
                setExtendedHoursData(prev => ({
                    ...prev,
                    pre: {
                        gainers: response.data?.data?.gainers || [],
                        losers: response.data?.data?.losers || []
                    }
                }));
            } else if (status === 'after') {
                // Fetch after-market data
                const response = await axios.get('/api/extended-hours/movers/after');
                setExtendedHoursData(prev => ({
                    ...prev,
                    after: {
                        gainers: response.data?.data?.gainers || [],
                        losers: response.data?.data?.losers || []
                    }
                }));
            } else if (status === 'open') {
                // Fetch stock price change data (during market hours) - US only
                const [gainersResponse, losersResponse] = await Promise.all([
                    axios.get('/api/stock-price-change/top-movers?period=1D&type=gainers&limit=10&usOnly=true'),
                    axios.get('/api/stock-price-change/top-movers?period=1D&type=losers&limit=10&usOnly=true')
                ]);

                setMarketHoursData({
                    gainers: gainersResponse.data?.data || [],
                    losers: losersResponse.data?.data || []
                });
            }
        } catch (error) {
            console.error('Error fetching mini market movers data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Determine market status and fetch data on mount
    useEffect(() => {
        const initializeMarketStatus = async () => {
            const status = await getMarketStatus(); // Now async
            setMarketStatus(status);
            fetchData(status);
        };

        initializeMarketStatus();

        // Optional: Refresh data every 5 minutes
        const interval = setInterval(async () => {
            const newStatus = await getMarketStatus();
            if (newStatus !== marketStatus) {
                setMarketStatus(newStatus);
                fetchData(newStatus);
            }
        }, 5 * 60 * 1000); // 5 minutes

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            {/* Pre-market movers */}
            {marketStatus === 'pre' && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <MiniExtendedHoursGainers
                            marketSession="pre"
                            data={extendedHoursData.pre.gainers}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MiniExtendedHoursLosers
                            marketSession="pre"
                            data={extendedHoursData.pre.losers}
                        />
                    </Grid>
                </Grid>
            )}

            {/* Market open - Stock price change movers (US only) */}
            {marketStatus === 'open' && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <MiniMarketHoursGainers data={marketHoursData.gainers} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MiniMarketHoursLosers data={marketHoursData.losers} />
                    </Grid>
                </Grid>
            )}

            {/* After-market movers */}
            {marketStatus === 'after' && (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <MiniExtendedHoursGainers
                            marketSession="after"
                            data={extendedHoursData.after.gainers}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MiniExtendedHoursLosers
                            marketSession="after"
                            data={extendedHoursData.after.losers}
                        />
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default MiniMarketMovers;